import React, { useEffect, useState } from "react";
import { BedDouble as BedDoubleIcon, CalendarCheck as CalendarCheckIcon, CalendarX as CalendarXIcon } from "lucide-react";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    CircularProgress,
    IconButton,
    Paper,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { format } from "date-fns";
import { Calendar } from "../../../../../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../../../../../components/ui/popover";
import { cn } from "../../../../../../lib/utils";
import styles from "./UpdateBedAvailability.module.scss";
import { getViewBedDuration, updateBedDuration } from "../../../../../../api/services/hostService";
import { useUserContext } from "../../../../../protectedRoute/UserContext";
import dayjs, { Dayjs } from "dayjs";
import { IViewBedDurationReponse } from "../../../../../model/hostModel";

interface Bed {
    id: string; // This is the BedAvailability ID
    bed?: string; // The ID of the related Bed
    bed_category: string | null;
    capacity?: number;
    start_date: Dayjs | null;
    end_date: Dayjs | null;
    property?: string;
}

interface UpdateBedAvailabilityProps {
    bed?: any;
    onBedAvailabilityUpdated: () => void;
    onCancel?: () => void;
}

const UpdateBedAvailability: React.FC<UpdateBedAvailabilityProps> = ({ bed, onBedAvailabilityUpdated, onCancel }) => {
    const [beds, setBeds] = useState<Bed[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingBed, setEditingBed] = useState<Bed | null>(() =>
        bed ? {
            id: bed.id,
            bed: bed.bed,
            bed_category: bed.bed_category || null,
            capacity: bed.capacity,
            start_date: bed.start_date ? dayjs(bed.start_date) : null,
            end_date: bed.end_date ? dayjs(bed.end_date) : null,
            property: bed.property,
        } : null
    );
    const [loading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const { state } = useUserContext();
    const { propertyID } = state;

    // Fetch initial data (only used for standalone table view when no bed prop is passed)
    useEffect(() => {
        const fetchBeds = async () => {
            if (!propertyID) {
                console.error("propertyID is null or undefined.");
                return;
            }
            console.log("Fetching beds for propertyID:", propertyID);

            setLoading(true);
            try {
                const fetchedBeds: IViewBedDurationReponse[] = await getViewBedDuration(propertyID);
                console.log("Fetched beds from API:", fetchedBeds);
                const processedBeds = fetchedBeds.map((bedAvailability) => ({
                    id: bedAvailability.id,
                    bed: bedAvailability.bed,
                    bed_category: bedAvailability.bed_category || null,
                    capacity: bedAvailability.capacity,
                    start_date: bedAvailability.start_date ? dayjs(bedAvailability.start_date) : null,
                    end_date: bedAvailability.end_date ? dayjs(bedAvailability.end_date) : null,
                    property: bedAvailability.property,
                }));
                console.log("Processed beds for state:", processedBeds);
                setBeds(processedBeds);
            } catch (error) {
                console.error("Failed to fetch beds:", error);
            } finally {
                setLoading(false);
            }
        };

        if (!bed) {
            fetchBeds();
        }
    }, [propertyID]);

    console.log("Current beds state:", beds);

    const handleEditBed = (bed: Bed) => {
        setEditingBed(bed);
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setEditingBed(null);
    };

    const handleSaveBed = async () => {
        if (editingBed && propertyID) {
            try {
                const availabilityId = editingBed.id; // Use the BedAvailability ID
                const startDate: string | undefined | null = editingBed.start_date ? editingBed.start_date.format("YYYY-MM-DD") : null;
                const endDate: string | undefined | null = editingBed.end_date ? editingBed.end_date.format("YYYY-MM-DD") : null;
    
                await updateBedDuration(availabilityId, {
                    start_date: startDate === null ? undefined : startDate,
                    end_date: endDate === null ? undefined : endDate,
                });
                onBedAvailabilityUpdated();
            } catch (error) {
                console.error("Failed to save bed:", error);
            } finally {
                handleDialogClose();
            }
        }
    };
    

    if (loading) {
        return <CircularProgress />;
    }

    if (bed) {
        return (
            <div className="space-y-5 py-2">
                {/* Bed info summary */}
                <div className="bg-muted/50 rounded-lg p-4 flex items-center gap-4">
                    <div className="p-3 bg-primary/10 rounded-lg">
                        <BedDoubleIcon className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                        <p className="font-semibold">{editingBed?.bed_category || "Bed"}</p>
                        <p className="text-sm text-muted-foreground">Capacity: {editingBed?.capacity ?? "—"}</p>
                    </div>
                </div>

                {/* Date inputs */}
                <div className="space-y-4">
                    <div className="space-y-2">
                        <label className="flex items-center gap-2 text-sm font-medium">
                            <CalendarCheckIcon className="h-4 w-4 text-green-600" />
                            Available From
                        </label>
                        <Popover>
                            <PopoverTrigger asChild>
                                <button
                                    className={cn(
                                        "w-full flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm text-left hover:bg-accent transition-colors",
                                        !editingBed?.start_date && "text-muted-foreground"
                                    )}
                                >
                                    <CalendarCheckIcon className="h-4 w-4 text-green-600 shrink-0" />
                                    {editingBed?.start_date
                                        ? format(editingBed.start_date.toDate(), "PPP")
                                        : "Pick a start date"}
                                </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                    mode="single"
                                    selected={editingBed?.start_date ? editingBed.start_date.toDate() : undefined}
                                    onSelect={(date) =>
                                        setEditingBed((prev) =>
                                            prev ? { ...prev, start_date: date ? dayjs(date) : null } : null
                                        )
                                    }
                                    disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                                    initialFocus
                                />
                            </PopoverContent>
                        </Popover>
                    </div>
                    <div className="space-y-2">
                        <label className="flex items-center gap-2 text-sm font-medium">
                            <CalendarXIcon className="h-4 w-4 text-red-500" />
                            Available Until
                        </label>
                        <Popover>
                            <PopoverTrigger asChild>
                                <button
                                    className={cn(
                                        "w-full flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm text-left hover:bg-accent transition-colors",
                                        !editingBed?.end_date && "text-muted-foreground"
                                    )}
                                >
                                    <CalendarXIcon className="h-4 w-4 text-red-500 shrink-0" />
                                    {editingBed?.end_date
                                        ? format(editingBed.end_date.toDate(), "PPP")
                                        : "Pick an end date"}
                                </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                    mode="single"
                                    selected={editingBed?.end_date ? editingBed.end_date.toDate() : undefined}
                                    onSelect={(date) =>
                                        setEditingBed((prev) =>
                                            prev ? { ...prev, end_date: date ? dayjs(date) : null } : null
                                        )
                                    }
                                    disabled={(date) =>
                                        date < new Date(new Date().setHours(0, 0, 0, 0)) ||
                                        (editingBed?.start_date ? date <= editingBed.start_date.toDate() : false)
                                    }
                                    initialFocus
                                />
                            </PopoverContent>
                        </Popover>
                    </div>
                </div>

                {/* Tip */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-700">
                        💡 <strong>Tip:</strong> Setting dates will automatically list your bed for travelers to book.
                    </p>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-2">
                    <button
                        onClick={onCancel}
                        className="px-4 py-2 rounded-md border border-input text-sm font-medium hover:bg-muted transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSaveBed}
                        disabled={isSaving}
                        className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
                    >
                        {isSaving ? "Saving..." : (editingBed?.start_date && editingBed?.end_date ? "Update & List" : "Save Changes")}
                    </button>
                </div>
            </div>
        )
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box className="p-6 bg-white rounded-lg shadow-lg">
                <Typography variant="h4" sx={{ color: '#1B3A32', fontWeight: 'bold', mb: 3 }}>
                    Bed Availability
                </Typography>

                <TableContainer component={Paper} sx={{ boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderRadius: '8px', overflow: 'hidden' }}>
                    <Table>
                        <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', color: '#1B3A32' }}>Category</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: '#1B3A32' }}>Capacity</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: '#1B3A32' }}>From Date</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: '#1B3A32' }}>To Date</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: '#1B3A32' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {beds.map((bed) => (
                                <TableRow key={bed.id} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' }, '&:hover': { backgroundColor: '#f0f0f0' } }}>
                                    <TableCell>{bed.bed_category || "Not Set"}</TableCell>
                                    <TableCell>{bed.capacity}</TableCell>
                                    <TableCell>
                                        {bed.start_date
                                            ? bed.start_date.format("MM/DD/YYYY")
                                            : "Not Set"}
                                    </TableCell>
                                    <TableCell>
                                        {bed.end_date
                                            ? bed.end_date.format("MM/DD/YYYY")
                                            : "Not Set"}
                                    </TableCell>
                                    <TableCell>
                                        <IconButton
                                            color="primary"
                                            onClick={() => handleEditBed(bed)}
                                            sx={{ color: '#1B3A32', '&:hover': { color: '#0f2d27' } }}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Dialog for updating bed details */}
                <Dialog open={dialogOpen} onClose={handleDialogClose}>
                    <DialogTitle sx={{ color: '#1B3A32', fontWeight: 'bold' }}>Update Bed</DialogTitle>
                    <DialogContent sx={{ minWidth: 320 }}>
                        <TextField
                            fullWidth
                            margin="dense"
                            label="Category"
                            value={editingBed?.bed_category || ""}
                            disabled={true}
                            onChange={(e) =>
                                setEditingBed((prev) =>
                                    prev ? { ...prev, bed_category: e.target.value } : null
                                )
                            }
                            sx={{
                                mb: 2,
                                '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: '#1B3A32' } },
                                '& .MuiInputLabel-root.Mui-focused': { color: '#1B3A32' },
                            }}
                        />
                        <div className="space-y-3">
                            <div className="space-y-1">
                                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                    <CalendarCheckIcon className="h-4 w-4 text-green-600" />
                                    From Date
                                </label>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <button
                                            className={cn(
                                                "w-full flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-left hover:bg-gray-50 transition-colors",
                                                !editingBed?.start_date && "text-gray-400"
                                            )}
                                        >
                                            <CalendarCheckIcon className="h-4 w-4 text-green-600 shrink-0" />
                                            {editingBed?.start_date
                                                ? format(editingBed.start_date.toDate(), "PPP")
                                                : "Pick a start date"}
                                        </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                        <Calendar
                                            mode="single"
                                            selected={editingBed?.start_date ? editingBed.start_date.toDate() : undefined}
                                            onSelect={(date) =>
                                                setEditingBed((prev) =>
                                                    prev ? { ...prev, start_date: date ? dayjs(date) : null } : null
                                                )
                                            }
                                            disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                                            initialFocus
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>
                            <div className="space-y-1">
                                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                    <CalendarXIcon className="h-4 w-4 text-red-500" />
                                    To Date
                                </label>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <button
                                            className={cn(
                                                "w-full flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-left hover:bg-gray-50 transition-colors",
                                                !editingBed?.end_date && "text-gray-400"
                                            )}
                                        >
                                            <CalendarXIcon className="h-4 w-4 text-red-500 shrink-0" />
                                            {editingBed?.end_date
                                                ? format(editingBed.end_date.toDate(), "PPP")
                                                : "Pick an end date"}
                                        </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                        <Calendar
                                            mode="single"
                                            selected={editingBed?.end_date ? editingBed.end_date.toDate() : undefined}
                                            onSelect={(date) =>
                                                setEditingBed((prev) =>
                                                    prev ? { ...prev, end_date: date ? dayjs(date) : null } : null
                                                )
                                            }
                                            disabled={(date) =>
                                                date < new Date(new Date().setHours(0, 0, 0, 0)) ||
                                                (editingBed?.start_date ? date <= editingBed.start_date.toDate() : false)
                                            }
                                            initialFocus
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>
                        </div>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDialogClose} sx={{ color: '#16a34a' }}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSaveBed}
                            variant="contained"
                            sx={{ backgroundColor: '#16a34a', color: 'white', '&:hover': { backgroundColor: '#15803d' } }}
                            disabled={isSaving}
                        >
                            {isSaving ? <CircularProgress size={24} /> : "Save"}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </LocalizationProvider>
    );
};

export default UpdateBedAvailability;
